import { createClient } from '@supabase/supabase-js';
import { LeaderboardUser } from '@/types/leaderboard';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const supabase = createClient(supabaseUrl, supabaseKey);

// Fallback mock data in case Supabase functions are not available - use actual user IDs if available
const fallbackMockData: LeaderboardUser[] = [
  { id: "d5eadcab-1ba1-4866-887a-8beeb134cdb8", name: 'Tamika Fillcot', username: '@tamikafillcot', avatar: '/placeholder.svg', level: 28, score: 1520, rank: 1, change: 2 },
  { id: "f7957368-81c7-428e-b992-8f47076b8b47", name: 'Patrick Newman', username: '@patricknewman', avatar: '/placeholder.svg', level: 24, score: 1340, rank: 2, change: -1 },
  { id: "4982e951-dec5-4962-a2d6-397974c152b0", name: 'Yulia Polischuk', username: '@meisquare', avatar: '/placeholder.svg', level: 19, score: 1120, rank: 3, change: 1 },
  { id: "d5eadcab-1ba1-4866-887a-8beeb134cdb8", name: 'Amanda Freeze', username: '@meow_amanda97', avatar: '/placeholder.svg', level: 16, score: 980, rank: 4, change: -2 },
  { id: "f7957368-81c7-428e-b992-8f47076b8b47", name: 'Anatoly Belik', username: '@belik_anatoly', avatar: '/placeholder.svg', level: 15, score: 860, rank: 5, change: 0 }
];

/**
 * Function to get all-time leaderboard data
 */
export const getAllTimeLeaderboard = async (): Promise<LeaderboardUser[]> => {
  try {
    const { data, error } = await supabase.rpc('get_all_time_leaderboard');
    
    if (error) {
      console.error('Error fetching all-time leaderboard:', error);
      return fallbackMockData.slice().sort((a, b) => (b.score * b.level) - (a.score * a.level));
    }
    
    if (!data || data.length === 0) {
      console.warn('No data returned from all-time leaderboard function, using fallback data');
      return fallbackMockData.slice().sort((a, b) => (b.score * b.level) - (a.score * a.level));
    }
    
    // Map the data to match the LeaderboardUser interface
    return data.map((user: any, index: number) => ({
      id: user.id, // Use the actual UUID from the database
      name: user.name || 'Anonymous User',
      username: user.username || '@unknown',
      avatar: user.avatar || '/placeholder.svg',
      level: user.level || 1,
      score: parseInt(user.score) || 0,
      rank: index + 1, // Rank based on sorted position
      change: 0, // Default value for change
    }));
  } catch (error) {
    console.error('Unexpected error fetching all-time leaderboard:', error);
    return fallbackMockData.slice().sort((a, b) => (b.score * b.level) - (a.score * a.level));
  }
};

/**
 * Function to get weekly leaderboard data
 */
export const getWeeklyLeaderboard = async (): Promise<LeaderboardUser[]> => {
  try {
    const { data, error } = await supabase.rpc('get_weekly_leaderboard');
    
    if (error) {
      console.error('Error fetching weekly leaderboard:', error);
      return fallbackMockData; // Use default mock data as fallback
    }
    
    if (!data || data.length === 0) {
      console.warn('No data returned from weekly leaderboard function, using fallback data');
      return fallbackMockData;
    }
    
    return data.map((user: any, index: number) => ({
      id: user.id, // Use the actual UUID from the database
      name: user.name || 'Anonymous User',
      username: user.username || '@unknown',
      avatar: user.avatar || '/placeholder.svg',
      level: user.level || 1,
      score: parseInt(user.score) || 0,
      rank: index + 1,
      change: Math.floor(Math.random() * 7) - 3, // Random change between -3 and 3 for demo
    }));
  } catch (error) {
    console.error('Unexpected error fetching weekly leaderboard:', error);
    return fallbackMockData;
  }
};

/**
 * Function to get monthly leaderboard data
 */
export const getMonthlyLeaderboard = async (): Promise<LeaderboardUser[]> => {
  try {
    const { data, error } = await supabase.rpc('get_monthly_leaderboard');
    
    if (error) {
      console.error('Error fetching monthly leaderboard:', error);
      return fallbackMockData.slice().sort((a, b) => b.level - a.level); // Use default mock data as fallback
    }
    
    if (!data || data.length === 0) {
      console.warn('No data returned from monthly leaderboard function, using fallback data');
      return fallbackMockData.slice().sort((a, b) => b.level - a.level);
    }
    
    return data.map((user: any, index: number) => ({
      id: user.id, // Use the actual UUID from the database
      name: user.name || 'Anonymous User',
      username: user.username || '@unknown',
      avatar: user.avatar || '/placeholder.svg',
      level: user.level || 1,
      score: parseInt(user.score) || 0,
      rank: index + 1,
      change: Math.floor(Math.random() * 5) - 2, // Random change between -2 and 2 for demo
    }));
  } catch (error) {
    console.error('Unexpected error fetching monthly leaderboard:', error);
    return fallbackMockData.slice().sort((a, b) => b.level - a.level);
  }
}; 